import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Chip,
  Button,
  CircularProgress,
  Paper,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import MailIcon from '@mui/icons-material/Mail';
import FolderIcon from '@mui/icons-material/Folder';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import custodiaService from '../services/custodiaService';

const TABS = [
  { label: 'Documentos', type: 'document', icon: <DescriptionIcon fontSize="small" /> },
  { label: 'Correspondencias', type: 'correspondence', icon: <MailIcon fontSize="small" /> },
  { label: 'Expedientes', type: 'proceeding', icon: <FolderIcon fontSize="small" /> },
];

const TYPE_COLORS = {
  document: '#1565c0',
  correspondence: '#7b1fa2',
  proceeding: '#2e7d32',
};

const EntitySelector = ({ onStart, loading: externalLoading }) => {
  const [tab, setTab] = useState(0);
  const [selected, setSelected] = useState([]);
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setOptions([]); return; }
    setSearching(true);
    try {
      const res = await custodiaService.searchSources(query, TABS[tab].type);
      setOptions(res.data?.data || []);
    } catch {
      setOptions([]);
    } finally {
      setSearching(false);
    }
  }, [tab]);

  const handleSelect = (_, value) => {
    if (!value) return;
    if (selected.find(s => s.id === value.id && s.type === value.type)) return;
    setSelected(prev => [...prev, value]);
  };

  const handleRemove = (item) => {
    setSelected(prev => prev.filter(s => !(s.id === item.id && s.type === item.type)));
  };

  const handleStart = () => {
    const sourceConfig = {
      documentIds: selected.filter(s => s.type === 'document').map(s => s.id),
      correspondenceIds: selected.filter(s => s.type === 'correspondence').map(s => s.id),
      proceedingIds: selected.filter(s => s.type === 'proceeding').map(s => s.id),
    };
    onStart(sourceConfig);
  };

  const counts = {
    document: selected.filter(s => s.type === 'document').length,
    correspondence: selected.filter(s => s.type === 'correspondence').length,
    proceeding: selected.filter(s => s.type === 'proceeding').length,
  };
  const total = selected.length;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', py: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>
        Nueva Conversación
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        Seleccione los documentos, correspondencias o expedientes que desea consultar con IA.
      </Typography>

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => { setTab(v); setOptions([]); }}
          sx={{ mb: 2, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 42 } }}
        >
          {TABS.map((t, i) => (
            <Tab
              key={i}
              icon={t.icon}
              iconPosition="start"
              label={`${t.label}${counts[t.type] ? ` (${counts[t.type]})` : ''}`}
            />
          ))}
        </Tabs>

        <Autocomplete
          options={options}
          getOptionLabel={(opt) => opt.name || ''}
          filterOptions={(x) => x}
          onInputChange={(_, value) => handleSearch(value)}
          onChange={handleSelect}
          value={null}
          loading={searching}
          noOptionsText="Escriba para buscar..."
          loadingText="Buscando..."
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={`${option.type}-${option.id}`} {...rest}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                  {option.documentCount != null && (
                    <Chip label={`${option.documentCount} docs`} size="small" variant="outlined" />
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={`Buscar ${TABS[tab].label.toLowerCase()}...`}
              size="small"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selected.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((item) => (
              <Chip
                key={`${item.type}-${item.id}`}
                label={item.name}
                size="small"
                onDelete={() => handleRemove(item)}
                sx={{
                  bgcolor: `${TYPE_COLORS[item.type]}15`,
                  color: TYPE_COLORS[item.type],
                  fontWeight: 600,
                  '& .MuiChip-deleteIcon': { color: TYPE_COLORS[item.type] },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>

      {total > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {[
              counts.document && `${counts.document} documento${counts.document > 1 ? 's' : ''}`,
              counts.correspondence && `${counts.correspondence} correspondencia${counts.correspondence > 1 ? 's' : ''}`,
              counts.proceeding && `${counts.proceeding} expediente${counts.proceeding > 1 ? 's' : ''}`,
            ].filter(Boolean).join(', ')}
          </Typography>
          <Button
            variant="contained"
            startIcon={externalLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            onClick={handleStart}
            disabled={externalLoading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {externalLoading ? 'Preparando...' : 'Iniciar Conversación'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EntitySelector;
